import axios from 'axios';
import { httpApi } from './http';
import type { RewardApiResponse, RewardPaginationParams } from '../types/reward';
import type { RewardsV1SearchResponse } from '../types/rewardSearchV1';
const api = httpApi;

export class RewardService {
  static async searchRewardsV1(query: string, opts?: { userId?: string; cardId?: string }): Promise<RewardsV1SearchResponse> {
    try {
      const response = await httpApi.get<RewardsV1SearchResponse>('/rewardsv1/search', {
        params: {
          q: query,
          ...(opts?.userId ? { userid: opts.userId } : {}),
          ...(opts?.cardId ? { cardid: opts.cardId } : {}),
        },
      });

      if (!response.data) {
        throw new Error('No data received from server');
      }

      if (!response.data.success) {
        throw new Error(response.data.error || 'API request failed');
      }

      return response.data;
    } catch (error) {
      console.error('Error searching rewards v1:', error);
      if (axios.isAxiosError(error)) {
        throw new Error((error.response?.data as any)?.message || 'Failed to search rewards');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  static async getRewards(params: RewardPaginationParams): Promise<RewardApiResponse> {
    try {
      const response = await api.post('/rewards', params);
      return response.data;
    } catch (error) {
      console.error('Error fetching rewards:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to fetch rewards');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  static async getRewardById(id: string) {
    try {
      const response = await api.get(`/rewards/${id}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching reward:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to fetch reward');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  static async createReward(rewardData: Omit<any, 'id'>) {
    try {
      const response = await api.post('/rewards/create', rewardData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating reward:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to create reward');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  static async updateReward(id: string, rewardData: any) {
    try {
      const response = await api.put(`/rewards/${id}`, rewardData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating reward:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to update reward');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  static async deleteReward(id: string) {
    try {
      const response = await api.delete(`/rewards/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting reward:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to delete reward');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  static async searchRewards(params: { q: string; fields?: string; limit?: number }) {
    try {
      const response = await api.post('/rewards/search', params);
      return response.data;
    } catch (error) {
      console.error('Error searching rewards:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to search rewards');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  static async getCategories() {
    try {
      const response = await api.get('/rewards/categories');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to fetch categories');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  static async getSubcategories(category?: string) {
    try {
      const response = await api.get('/rewards/subcategories', {
        params: category ? { category } : undefined,
      });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to fetch subcategories');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  static async getBanks() {
    try {
      const response = await api.get('/rewards/banks');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching banks:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to fetch banks');
      }
      throw new Error('An unexpected error occurred');
    }
  }
}